import axios from 'axios'
import config from '../../../config'
import {
    TenantSetupStatusResponse,
    CustomerSuccessOption,
    ModuleCatalogEntry,
} from '../types/types'
import { VendorDetail } from '../../TenantForm/types/types'

const { apiServerUrl } = config
const ONBOARD_BASE = `${apiServerUrl}/admin/tenant-onboard`

export { executeOnboardStep } from '../../TenantForm/services/api'

export {
    getTenantDetails as getSubscriptionsByBusiness,
    getSubscriptionDetails,
} from '../../Subscriptions/services/api'
export {
    postSubscriptionDetails,
    editSubscriptionDetails,
} from '../../Subscriptions/components/CreateSubscription/services/api'

export const getTenantList = (
    page: number,
    rows: number,
    searchFilter: string
) =>
    axios
        .get(
            `${apiServerUrl}/admin/tenant-onboard?page=${page}&limit=${rows}&searchTerm=${searchFilter}`
        )
        .then(({ data }) => data)
export const getSetupStatus = async (
    tenantId: number
): Promise<TenantSetupStatusResponse> => {
    const { data } = await axios.get<TenantSetupStatusResponse>(
        `${ONBOARD_BASE}/${tenantId}/status`
    )
    return data
}

export const getLandingData = async (
    tenantId: number
): Promise<TenantSetupStatusResponse> => {
    try {
        const { data } = await axios.get<TenantSetupStatusResponse>(
            `${ONBOARD_BASE}/${tenantId}/landing`
        )
        return data
    } catch (err: any) {
        // fall back to /status if /landing not yet deployed
        if (err?.response?.status === 404) {
            return getSetupStatus(tenantId)
        }
        throw err
    }
}

export const getDefaultOirModules = async (): Promise<ModuleCatalogEntry[]> => {
    try {
        const { data } = await axios.get<{ modules: ModuleCatalogEntry[] }>(
            `${ONBOARD_BASE}/default-oir-modules`
        )
        return data?.modules ?? []
    } catch (err) {
        console.error('Failed to fetch default OIR modules', err)
        return []
    }
}

export const getCustomerSuccessOptions = async (): Promise<CustomerSuccessOption[]> => {
    try {
        const { data } = await axios.get<CustomerSuccessOption[]>(
            `${ONBOARD_BASE}/customer-success-options`
        )
        return data ?? []
    } catch (err) {
        console.error('Failed to fetch customer success options', err)
        return []
    }
}

export const createSubscriptionViaOnboard = async (
    payload: Record<string, unknown>
): Promise<number> => {
    try {
        const res = await axios.post(`${ONBOARD_BASE}/`, payload)
        return res.status
    } catch (err: any) {
        const data = err?.response?.data
        if (data?.message) {
            // eslint-disable-next-line no-throw-literal
            throw {
                message: data.message,
                nextSteps: data.nextSteps || [],
            }
        }
        throw new Error(err?.message || 'Failed to create subscription')
    }
}

export const editSubscriptionViaOnboard = async (
    payload: Record<string, unknown>
): Promise<number> => {
    try {
        const res = await axios.put(`${ONBOARD_BASE}/`, payload)
        return res.status
    } catch (err: any) {
        const data = err?.response?.data
        if (data?.message) {
            // eslint-disable-next-line no-throw-literal
            throw {
                message: data.message,
                nextSteps: data.nextSteps || [],
            }
        }
        throw new Error(err?.message || 'Failed to update subscription')
    }
}

export const updateRedmineOwners = async (
    tenantId: number,
    owners: Array<{ moduleId: number; userId: number | null }>
): Promise<{ redmineWarning: string | null }> => {
    try {
        const { data } = await axios.put(
            `${ONBOARD_BASE}/${tenantId}/redmine-owners`,
            { owners }
        )
        return { redmineWarning: data?.data?.redmineWarning ?? null }
    } catch (err: any) {
        const data = err?.response?.data
        // eslint-disable-next-line no-throw-literal
        throw {
            message: data?.message || 'Failed to update Redmine owners',
            nextSteps: data?.nextSteps || [],
        }
    }
}

export const getVendorConfig = async (
    businessId: number,
    businessName: string,
    mode: string
): Promise<VendorDetail[]> => {
    try {
        const { data } = await axios.get<VendorDetail[]>(
            `${apiServerUrl}/users/notification/config?mode=${mode}`,
            {
                headers: {
                    'business-id': businessId.toString(),
                    'business-name': businessName,
                },
            }
        )
        return data || []
    } catch (err: any) {
        console.error(`Failed to fetch vendor config for mode ${mode}`, err)
        return []
    }
}

export const updateTenantSettings = async (
    tenantId: number,
    payload: { billingLevel?: 'tenant' | 'business'; customerSuccess?: string | null; zohoCustomerName?: string | null }
): Promise<void> => {
    try {
        await axios.put(`${ONBOARD_BASE}/${tenantId}`, payload)
    } catch (err: any) {
        const data = err?.response?.data
        // eslint-disable-next-line no-throw-literal
        throw { message: data?.message || 'Failed to update tenant settings', nextSteps: data?.nextSteps || [] }
    }
}

export const getCustomerDetails = async (
    mobile: string,
    businessId: number,
    businessName: string
): Promise<any> => {
    try {
        const { data } = await axios.get(
            `${config.ruleServerUrl}/users/customer-details`,
            {
                params: { mobile, bu: businessId, isCustomerInfoOnly: false },
                headers: {
                    'business-id': businessId.toString(),
                    'business-name': businessName,
                },
            }
        )
        return data
    } catch (err: any) {
        console.error('Failed to fetch customer details', err)
        throw err
    }
}

export * from './escalationApi'

export * from './oirLockApi'
